/**
 * `/reprint-queue` — the shared, cross-station list of garments waiting to be reprinted, and the one
 * button that turns them into film.
 *
 * The problem it solves, in the owner's words: "you end up with three batches, and we have the exact
 * same problem, wasting film." A transfer ruined at the cutting bench, three ruined at the heat press
 * and a parcel lost by the carrier are three sheets and three print/powder/cure cycles today. Three
 * stations on three devices report into ONE server-side list here, and one flush prints one sheet.
 *
 * ── Two things this page must never do ─────────────────────────────────────────────────────────────
 *  1. Make a garment disappear. Every queued garment blocks its order from the ship lanes, so a row
 *     that quietly vanishes from this list is a box that ships a shirt short. Dismissing is therefore
 *     manager-only, needs a reason, and is refused outright once the garment is on film.
 *  2. Spend film without saying what it costs. The Print button does NOT print — it asks the server,
 *     which refuses with the exact cost (garments · replacement blanks · sheets) and only prints on a
 *     second, explicitly confirmed call. The cost is quoted by the SERVER rather than computed here,
 *     because the server knows today's roll, the per-sheet cap and the store split.
 */
import { reprintPlacementsFor } from '@fsp/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, Printer, RefreshCw, Undo2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import {
  ageLabel,
  crossRunBadge,
  crossRunReview,
  estimateCost,
  garmentLabel,
  lineKeyOf,
  queueAge,
  quoteDrift,
  REASON_LABEL,
  type ReprintQueueEntry,
  returnedBadge,
  returnedNeedsReview,
  runKeyOf,
  STATION_LABEL,
  scopeByLine,
  sortQueue,
} from './reprint-queue-lib';

/** The server's refusal shapes this page knows how to render. Anything else falls back to `detail`. */
type FlushCost = {
  garments: number;
  /** Films the sheet will carry. Moves independently of `garments` now that a garment can be
   *  reprinted at one of its prints instead of all of them. */
  transfers: number;
  replacementBlanks: number;
  sheets: number;
  /** Rendered beside the cost. Absent from an older server, and absent when there is nothing to say. */
  crossRunWarnings?: CrossRunWarning[];
};
type FlushBlocker = {
  storeId: string;
  orderId: string;
  lineId: string;
  orderName?: string;
  /** WHICH production run was refused. Part of the React key: a line can now be refused once per run, and
   *  two blockers with the same (store, order, line, reason) would otherwise collide on one key. */
  cohortKey?: string;
  entryIds: string[];
  reason: string;
  detail: string;
};
/** An aggregate the server cannot decide for us: this line's reprints span more than one run. */
type CrossRunWarning = {
  storeId: string;
  orderId: string;
  lineId: string;
  orderName?: string;
  runs: number;
  claiming: number;
  onFilm: number;
  largestRun: number;
  detail: string;
};

function useReprintQueue() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['reprint-queue'],
    queryFn: async () => {
      const { data, error } = await client.GET('/batches/reprint-cart/entries');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the reprint queue.');
      // `checked` is a literal `true` in the contract precisely so an empty list can never be
      // mistaken for a failed read — the server throws rather than degrading to [].
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data.entries;
    },
  });
}

export function ReprintQueuePage() {
  const query = useReprintQueue();
  const queryClient = useQueryClient();
  const client = useApiClient();
  const { state: authState } = useAuth();
  // Cosmetic only — the server gate is authoritative (403 + a reason requirement on the dismiss).
  const isManager = authState.status === 'signed-in' && authState.role.isAdmin;

  const entries = useMemo(() => sortQueue(query.data ?? []), [query.data]);
  const waiting = entries.filter((e) => !e.flushedIntoBatchId);
  const onFilm = entries.filter((e) => e.flushedIntoBatchId);

  // Selection is by entryId and defaults to "everything waiting" — the common case is print it all,
  // and an empty default would make the primary button dead on arrival.
  //
  // ⚠️ WITH ONE EXCEPTION, and it is a money gate rather than a nicety. A garment whose replacement was
  // ALREADY committed to film comes back to this list (the mark-printed fall-through un-flushes it so it
  // cannot strand), and pre-selecting it means the operator's default action prints a SECOND film, burns
  // a SECOND cure cycle and draws a SECOND blank for one shirt. That is the reprint-queue duplicate
  // leak, and this list is the last place it can be stopped. `returnedNeedsReview` rows must therefore
  // be ticked DELIBERATELY. A row whose sheet was deleted before printing was never made and stays
  // selected — see the helper for why the marker is read before the reason.
  const [deselected, setDeselected] = useState<ReadonlySet<string>>(new Set());
  // Ticking a review row is an explicit OPT-IN, so it needs its own state: `deselected` cannot express it
  // (removing an id from a deselect set would not overcome the review rule). Two pieces of state, but ONE
  // predicate reads them — `isSelected` below is the single source of truth for "is this going on the
  // sheet", so the checkbox, the quote, the cost and the POST body can never disagree about which rows
  // are in.
  //
  // ⚠️ A MAP keyed to the RETURN the consent was given for, not a Set of ids. A Set was the first
  // version and it re-opened the very leak this gate closes, one round trip later (found by adversarial
  // review, reproduced with the POST body captured): tick a review row, let anything else flush it — a
  // second station, a second tab, or just a LOST RESPONSE on shop wifi, since the flush deliberately
  // carries no idempotency token because "the queue rows are the ledger" — and the row leaves `waiting`
  // and later comes back. `entryId` survives an un-flush by design, so an id-keyed consent survived with
  // it and the row returned PRE-TICKED with no second consent. Nothing downstream catches that:
  // `unflush` does not bump `rev`, so the route's staleness check passes, and the server knows nothing
  // about `alreadyReprintedOnce` — this gate is client-side in its entirety.
  const [reviewed, setReviewed] = useState<ReadonlyMap<string, string>>(new Map());
  /**
   * What the operator's tick was consent FOR — a COMPOSITE, because there are now two reasons a row can
   * need one and they expire on different events.
   *
   *  · `returnedFromBatchId` — re-stamped by every `unflush`, and a batch id is never reused (a SHEET_GONE
   *    unbuild deletes the batch, a PRINTED one can never be flushed into again), so a round trip always
   *    changes the token and always expires stale consent.
   *  · the cross-run siblings and THEIR film state — so a tick given while the other run was merely queued
   *    expires the moment that run's film is committed, and a third run appearing re-asks.
   *
   * ⚠️ Both halves are needed. A cross-run row typically has NO return stamp, so on `returnedFromBatchId`
   * alone its token would be the constant `''` — consent that never expires, which is exactly the leak that
   * made the first version of this gate (a `Set` of entryIds) unsafe one round trip later.
   *
   * Deliberately NOT `rev` or `updatedAt`: both move when any station merely re-reports the same garment,
   * which would expire a fresh tick out from under the operator mid-quote. And the `?? ''` fallbacks
   * deliberately ALLOW consent for a row with neither marker rather than refusing it — refusing would leave
   * a row impossible to print with no operator exit, which is the worse failure.
   */
  /**
   * Rows gated because their LINE has garments in flight from more than one production run, mapped to the
   * token material that describes those siblings. Computed over EVERY entry, not just `waiting`: a sibling
   * run already on a sheet has spent its film and is the most dangerous kind, and it is not in `waiting`.
   */
  const crossRun = useMemo(() => crossRunReview(entries), [entries]);
  const needsReview = (e: ReprintQueueEntry): boolean =>
    returnedNeedsReview(e) || crossRun.has(e.entryId);
  const consentTokenOf = (e: ReprintQueueEntry): string =>
    `${e.returnedFromBatchId ?? ''}|${crossRun.get(e.entryId) ?? ''}`;
  const isSelected = (e: ReprintQueueEntry): boolean =>
    needsReview(e) ? reviewed.get(e.entryId) === consentTokenOf(e) : !deselected.has(e.entryId);
  const toggle = (e: ReprintQueueEntry) => {
    if (needsReview(e)) {
      setReviewed((prev) => {
        const next = new Map(prev);
        if (next.get(e.entryId) === consentTokenOf(e)) next.delete(e.entryId);
        else next.set(e.entryId, consentTokenOf(e));
        return next;
      });
      return;
    }
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(e.entryId)) next.delete(e.entryId);
      else next.add(e.entryId);
      return next;
    });
  };
  const selected = waiting.filter(isSelected);
  // What "Select all" may act on: everything EXCEPT the review rows, so a bulk control can never opt one
  // in. But the control's LABEL is driven by whether anything is selected at all, not by that subset —
  // otherwise a queue holding only review rows sits permanently on "Select all" while pressing it does
  // nothing, and a deliberate review tick has no bulk way back off. Derived from `isSelected` rather than
  // from set sizes, because with two sets "nothing deselected" stopped meaning "everything selected".
  const bulkable = waiting.filter((e) => !needsReview(e));
  const anySelected = selected.length > 0;
  // Nothing to add and nothing to clear — say so by disabling rather than offering a no-op.
  const bulkDisabled = !anySelected && bulkable.length === 0;

  /**
   * The exact refs the QUOTED cost was computed from.
   *
   * ⚠️ Not a convenience. The confirm call must send THIS, never a freshly-read `selected`: the
   * queue live-updates over the change feed, so a garment reported by another station between the
   * quote and the click would otherwise be added to a sheet whose cost nobody agreed to. The
   * server's `rev` conditions catch a CHANGED entry but not an ADDED one — the operator confirmed
   * "7 garments, 5 blanks" and would get 8 and 6.
   */
  const [quoted, setQuoted] = useState<Array<{ entryId: string; rev: number }> | null>(null);
  const [cost, setCost] = useState<FlushCost | null>(null);
  const [blockers, setBlockers] = useState<FlushBlocker[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const invalidate = () => {
    // The queue and the sheets both moved. `reprint-queue` also live-updates over the change feed
    // (REPRINTQ# publishes the `orders` domain), so this is belt-and-braces for the acting client.
    void queryClient.invalidateQueries({ queryKey: ['reprint-queue'] });
    void queryClient.invalidateQueries({ queryKey: ['batches'] });
    void queryClient.invalidateQueries({ queryKey: ['production-board'] });
  };

  const flush = useMutation({
    mutationFn: async (v: {
      entries: Array<{ entryId: string; rev: number }>;
      confirm: boolean;
    }) => {
      const { data, error, response } = await client.POST('/batches/reprint-cart/flush', {
        body: { entries: v.entries, ...(v.confirm ? { confirmFlush: true } : {}) },
      });
      if (error) {
        const body = error.error as Record<string, unknown> | undefined;
        return { kind: 'error' as const, status: response.status, body, sent: v.entries };
      }
      if (!data?.ok) throw new Error('Empty response from the server.');
      return { kind: 'ok' as const, data: data.data, sent: v.entries };
    },
    onSuccess: (res) => {
      setProblem(null);
      setBlockers(null);
      if (res.kind === 'ok') {
        setCost(null);
        setQuoted(null);
        const n = res.data.sheets.length;
        setResult(
          res.data.deduplicated
            ? 'Those garments were already on a sheet — nothing new was printed.'
            : `Printing ${n} sheet${n === 1 ? '' : 's'} · ${res.data.garments} garment${res.data.garments === 1 ? '' : 's'}.`,
        );
        setDeselected(new Set());
        // Clear the review opt-ins too. This is the tidy path, NOT the safety mechanism — it only runs in
        // the acting client's own success handler, so it cannot be what makes "consent is for ONE print"
        // true. The consent TOKEN is what holds that invariant, on every client and after a lost
        // response; see `consentTokenOf`.
        setReviewed(new Map());
        invalidate();
        return;
      }
      const type = res.body?.type;
      if (type === 'reprint_flush_confirmation_required') {
        // Freeze the exact refs this quote was computed from. The confirm sends THESE, so a garment
        // reported by another station while the dialog is open cannot ride along unpriced.
        setQuoted(res.sent);
        setCost({
          garments: Number(res.body?.garments ?? 0),
          transfers: Number(res.body?.transfers ?? 0),
          replacementBlanks: Number(res.body?.replacementBlanks ?? 0),
          sheets: Number(res.body?.sheets ?? 0),
          ...(Array.isArray(res.body?.crossRunWarnings)
            ? { crossRunWarnings: res.body.crossRunWarnings as CrossRunWarning[] }
            : {}),
        });
        return;
      }
      setQuoted(null);
      setCost(null);
      if (type === 'reprint_flush_blocked') {
        setBlockers((res.body?.blockers as FlushBlocker[]) ?? []);
        return;
      }
      // Stale / raced / nothing-queued all mean the same thing to a human: the list moved.
      setProblem(String(res.body?.detail ?? 'The flush was refused.'));
      if (type === 'reprint_flush_stale' || type === 'reprint_flush_raced') invalidate();
    },
    onError: (err) => setProblem(err instanceof Error ? err.message : 'Network error.'),
  });

  const estimate = estimateCost(selected);
  // Scope is a LINE decision, so it is computed once per line and shared by that line's rows — a badge
  // derived per entry claims a narrowing the sheet will not honour.
  // How many production runs each LINE has in flight — for the badge's copy. Derived from every entry for
  // the same reason `crossRun` is: a sibling run already on a sheet still counts.
  const runsOnLine = useMemo(() => {
    const out = new Map<string, number>();
    const seen = new Map<string, Set<string>>();
    for (const e of entries) {
      const key = lineKeyOf(e);
      const set = seen.get(key) ?? new Set<string>();
      set.add(e.cohortKey ?? '');
      seen.set(key, set);
    }
    for (const [key, set] of seen) out.set(key, set.size);
    return out;
  }, [entries]);
  const waitingScopes = useMemo(() => scopeByLine(waiting), [waiting]);
  const onFilmScopes = useMemo(() => scopeByLine(onFilm), [onFilm]);
  // Rows the gate is holding back: waiting, needing review, and not yet ticked. They are NOT "arrived
  // since the quote", and telling the operator to print them afterwards is the duplicate this page exists
  // to stop. Computed inline rather than memoized — it depends on two pieces of selection state plus the
  // cross-run set, and a dependency list over those is more likely to go stale than this loop is to cost
  // anything on a list the size of one day's defects.
  const heldBackIds = new Set(
    waiting.filter((e) => needsReview(e) && !isSelected(e)).map((e) => e.entryId),
  );
  const drift = quoteDrift(quoted ?? [], waiting, heldBackIds);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Reprint queue</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Every garment the shop still owes a reprint on, from all three stations. They keep their
            orders out of the ship lanes until the reprint is printed and pressed. Print them
            together and they share one sheet of film and one cure cycle instead of one each.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={query.isFetching ? 'size-4 animate-spin' : 'size-4'} />
            Refresh
          </Button>
          <Button
            size="sm"
            disabled={selected.length === 0 || flush.isPending}
            onClick={() => {
              setResult(null);
              flush.mutate({
                entries: selected.map((e) => ({ entryId: e.entryId, rev: e.rev })),
                confirm: false,
              });
            }}
          >
            {flush.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Printer className="size-4" />
            )}
            Print reprint sheet ({selected.length})
          </Button>
        </div>
      </header>

      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn't load the reprint queue</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Network error.'} Until this
            loads, treat every order as possibly having an outstanding reprint.
          </AlertDescription>
        </Alert>
      )}
      {problem && (
        <Alert variant="destructive">
          <AlertTitle>Nothing was printed</AlertTitle>
          <AlertDescription>{problem}</AlertDescription>
        </Alert>
      )}
      {result && (
        <Alert>
          <AlertTitle>Sent to the printer</AlertTitle>
          <AlertDescription>
            {result} Find it under Gang sheets — it is badged as a reprint.
          </AlertDescription>
        </Alert>
      )}
      {blockers && blockers.length > 0 && <BlockerList blockers={blockers} />}

      {query.isPending && <Skeleton className="h-48 w-full" />}

      {query.isSuccess && entries.length === 0 && (
        <div className="rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
          Nothing is waiting to be reprinted. Report a ruined garment from the press, the cutting
          bench or an order card and it will appear here.
        </div>
      )}

      {waiting.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">
              Waiting ({waiting.length}) — {estimate.replacementBlanks} replacement blank
              {estimate.replacementBlanks === 1 ? '' : 's'} if you print all
            </h2>
            <Button
              variant="ghost"
              size="sm"
              disabled={bulkDisabled}
              onClick={() => {
                if (anySelected) {
                  // "Select none" has to mean NONE, so it clears the review opt-ins as well —
                  // otherwise a deliberately ticked already-reprinted row would survive the clear and
                  // ride the next flush unnoticed.
                  setDeselected(new Set(waiting.map((e) => e.entryId)));
                  setReviewed(new Map());
                } else {
                  // ⚠️ "Select all" deliberately does NOT opt in the review rows. A bulk control must
                  // never be able to spend a duplicate film — those stay individually ticked.
                  setDeselected(new Set());
                }
              }}
            >
              {anySelected ? 'Select none' : 'Select all'}
            </Button>
          </div>
          <ul className="space-y-2">
            {waiting.map((e) => (
              <EntryRow
                key={e.entryId}
                entry={e}
                scope={waitingScopes.get(runKeyOf(e)) ?? reprintPlacementsFor([e])}
                crossRunRuns={runsOnLine.get(lineKeyOf(e)) ?? 1}
                crossRunGated={crossRun.has(e.entryId)}
                selected={isSelected(e)}
                onToggle={() => toggle(e)}
                isManager={isManager}
                onDismissed={invalidate}
              />
            ))}
          </ul>
        </section>
      )}

      {onFilm.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-medium">Already on a sheet ({onFilm.length})</h2>
          <p className="text-xs text-muted-foreground">
            These are on film and still blocking their orders — deliberately. They clear when their
            sheet is marked printed, and come back here if it is deleted.
          </p>
          <ul className="space-y-2">
            {onFilm.map((e) => (
              <EntryRow
                key={e.entryId}
                entry={e}
                scope={onFilmScopes.get(runKeyOf(e)) ?? reprintPlacementsFor([e])}
                crossRunRuns={runsOnLine.get(lineKeyOf(e)) ?? 1}
                crossRunGated={crossRun.has(e.entryId)}
                selected={false}
                isManager={isManager}
                onDismissed={invalidate}
              />
            ))}
          </ul>
        </section>
      )}

      <Dialog
        open={cost !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCost(null);
            setQuoted(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Print the reprint sheet?</DialogTitle>
            <DialogDescription>
              This spends film and pulls blanks. Nothing has been printed yet.
            </DialogDescription>
          </DialogHeader>
          {cost && quoted && drift.heldBack > 0 && (
            <p className="text-sm text-amber-600">
              {drift.heldBack} garment{drift.heldBack === 1 ? ' is' : 's are'} held back for review
              and {drift.heldBack === 1 ? 'is' : 'are'} NOT on this sheet.{' '}
              {drift.heldBack === 1 ? 'It' : 'They'} may already have been reprinted, or reported
              against another production run — check before printing{' '}
              {drift.heldBack === 1 ? 'it' : 'them'}.
            </p>
          )}
          {cost && quoted && drift.addedSinceQuote > 0 && (
            <p className="text-sm text-amber-600">
              {drift.addedSinceQuote} more garment{drift.addedSinceQuote === 1 ? ' has' : 's have'}{' '}
              been reported since this was priced.{' '}
              {drift.addedSinceQuote === 1 ? 'It is' : 'They are'} NOT on this sheet — print again
              afterwards to include {drift.addedSinceQuote === 1 ? 'it' : 'them'}.
            </p>
          )}
          {cost && (
            <div className="space-y-2 rounded-md border p-4 text-sm">
              <Row label="Garments" value={String(cost.garments)} />
              <Row
                label="Transfers on the film"
                value={String(cost.transfers)}
                hint={
                  // From the scope function over the same selection the server quoted — NOT inferred
                  // from the ratio of transfers to garments, which fired when nothing had been narrowed
                  // and stayed silent when everything had.
                  estimate.narrowedGarments > 0
                    ? `${estimate.narrowedGarments} garment${estimate.narrowedGarments === 1 ? '' : 's'} reprinting only the print that was ruined.`
                    : undefined
                }
              />
              <Row
                label="Replacement blanks to pull"
                value={String(cost.replacementBlanks)}
                hint={
                  cost.replacementBlanks < cost.garments
                    ? 'Keep-the-blank garments press onto the shirt they are already on.'
                    : undefined
                }
              />
              <Row
                label="Sheets of film"
                value={String(cost.sheets)}
                hint={
                  cost.sheets > 1
                    ? 'Split by the per-sheet cap, by store, or by production run.'
                    : undefined
                }
              />
            </div>
          )}
          {/* ⚠️ A RENDERED ROW, not a sentence folded into `detail` — this dialog builds itself from named
              fields and ignores `detail` entirely, so a warning hidden there would be invisible at the one
              moment it matters. The server cannot decide this: two runs of one line usually mean two
              genuinely different shirts, and no rank comparison can tell when they do not. */}
          {cost?.crossRunWarnings?.map((w) => (
            <p
              key={`${w.storeId}#${w.orderId}#${w.lineId}`}
              className="rounded-md border border-amber-500/50 p-3 text-sm text-amber-600"
            >
              <span className="font-medium">{w.orderName ?? w.orderId}</span> — {w.detail}
            </p>
          ))}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCost(null);
                setQuoted(null);
              }}
              disabled={flush.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (!quoted) return;
                // ⚠️ RE-CHECK BEFORE REPLAYING THE FROZEN REFS. Sending `quoted` rather than a fresh read
                // is deliberate — it stops a garment reported mid-dialog riding onto a sheet nobody priced
                // — but it also replays rows whose CONSENT has expired since the quote. That happens for
                // real: a cross-run tick expires the moment the sibling run's film is committed, which is
                // exactly when the row stops being a maybe and becomes a probable duplicate. The server
                // cannot catch it either; its `rev` guard only sees the selected row's own changes.
                const stale = quoted.filter(
                  (q) => !selected.some((e) => e.entryId === q.entryId),
                ).length;
                if (stale > 0) {
                  setCost(null);
                  setQuoted(null);
                  setProblem(
                    `${stale} garment${stale === 1 ? '' : 's'} in that quote need${stale === 1 ? 's' : ''} another look — something changed about ${stale === 1 ? 'it' : 'them'} while the dialog was open. Nothing was printed. Check the list and price it again.`,
                  );
                  return;
                }
                flush.mutate({ entries: quoted, confirm: true });
              }}
              disabled={flush.isPending || !quoted}
            >
              {flush.isPending && <Loader2 className="size-4 animate-spin" />}
              Print it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <div className="font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function BlockerList({ blockers }: { blockers: FlushBlocker[] }) {
  return (
    <Alert variant="destructive">
      <AlertTriangle className="size-4" />
      <AlertTitle>
        {/* "problems", not garments: a blocker is per (line, run, reason) and one of them can cover a
            whole line, so counting them as garments read "1 garment" while refusing the day's flush. */}
        {blockers.length} problem{blockers.length === 1 ? '' : 's'} — nothing was printed
      </AlertTitle>
      <AlertDescription>
        <ul className="mt-2 space-y-2">
          {blockers.map((b) => (
            <li key={`${b.storeId}#${b.orderId}#${b.lineId}#${b.cohortKey ?? ''}#${b.reason}`}>
              <span className="font-medium">{b.orderName ?? b.orderId}</span> — {b.detail}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}

function EntryRow({
  entry,
  scope,
  selected,
  onToggle,
  isManager,
  onDismissed,
  crossRunRuns,
  crossRunGated,
}: {
  entry: ReprintQueueEntry;
  /** The (LINE, RUN)'s reprint scope — never this entry's alone. See `scopeByLine` for why. */
  scope: ReturnType<typeof reprintPlacementsFor>;
  selected: boolean;
  onToggle?: () => void;
  isManager: boolean;
  onDismissed: () => void;
  /** How many production runs of this row's LINE are in flight. Set-derived, threaded in the same way
   *  `scope` is, because a row cannot see its siblings and a per-entry guess would lie. */
  crossRunRuns: number;
  /** True when THIS row is the one needing a deliberate tick because of those runs. */
  crossRunGated: boolean;
}) {
  const { hours, tone } = queueAge(entry.addedAt, Date.now());
  const onFilm = Boolean(entry.flushedIntoBatchId);
  const returned = returnedBadge(entry);
  // ⚠️ Only where there is something to DO about it. The "already on a sheet" list renders rows without a
  // checkbox, and a red "check before printing" badge on a row the operator cannot tick is a dead end.
  const crossRun =
    crossRunGated && onToggle
      ? crossRunBadge(crossRunRuns, entry.crossRunFlushedOnce === true)
      : null;
  return (
    <li className="flex items-start gap-3 rounded-lg border p-3">
      {onToggle ? (
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          aria-label={`Include ${garmentLabel(entry)}`}
          className="mt-1"
        />
      ) : (
        <span className="mt-1 size-4" aria-hidden />
      )}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{garmentLabel(entry)}</span>
          <Badge variant="outline">{REASON_LABEL[entry.reason]}</Badge>
          {entry.transferOnly === true && (
            <Badge variant="secondary" title="The shirt is fine — only the transfer is reprinted.">
              Keep the blank
            </Badge>
          )}
          {scope.narrowed && (
            <Badge
              variant="secondary"
              title={`Only the ${scope.placements.map((p) => p.location).join(' + ')} goes back on film for this order line. Its other prints were fine.`}
            >
              {scope.placements.map((p) => p.location).join(' + ')} only
            </Badge>
          )}
          {onFilm && <Badge variant="secondary">On a sheet</Badge>}
          {returned && (
            <Badge
              variant={returned.needsReview ? 'destructive' : 'outline'}
              className="gap-1"
              title={returned.title}
            >
              <Undo2 className="size-3" />
              {returned.label}
            </Badge>
          )}
          {crossRun && (
            <Badge variant="destructive" className="gap-1" title={crossRun.title}>
              <AlertTriangle className="size-3" />
              {crossRun.label}
            </Badge>
          )}
          <Badge
            variant={tone === 'stale' ? 'destructive' : 'outline'}
            className={tone === 'aging' ? 'border-amber-500 text-amber-600' : undefined}
          >
            {ageLabel(hours)}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">
          {(entry.stations ?? [entry.station]).map((s) => STATION_LABEL[s] ?? s).join(' + ')}
          {entry.sourceBatchSeq ? ` · off sheet #${entry.sourceBatchSeq}` : ''}
          {entry.note ? ` · ${entry.note}` : ''}
        </div>
      </div>
      <DismissButton entry={entry} isManager={isManager} onDone={onDismissed} />
    </li>
  );
}

/**
 * Dismiss WITHOUT reprinting — the one action on this page that can send an incomplete box out of
 * the door, so it is manager-only and needs a reason. The server refuses it outright once the
 * garment is on film; delete that sheet first and the garment comes back here.
 */
function DismissButton({
  entry,
  isManager,
  onDone,
}: {
  entry: ReprintQueueEntry;
  isManager: boolean;
  onDone: () => void;
}) {
  const client = useApiClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const dismiss = useMutation({
    mutationFn: async () => {
      const { error: err } = await client.DELETE('/batches/reprint-cart/entries/{entryId}', {
        params: { path: { entryId: entry.entryId } },
        body: { reason },
      });
      if (err) throw new Error(err?.error?.detail ?? 'Could not dismiss that garment.');
    },
    onSuccess: () => {
      setOpen(false);
      setReason('');
      setError(null);
      onDone();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Network error.'),
  });

  if (!isManager) return null;
  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label={`Dismiss ${garmentLabel(entry)}`}
        onClick={() => setOpen(true)}
      >
        <X className="size-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dismiss without reprinting?</DialogTitle>
            <DialogDescription>
              {garmentLabel(entry)}. This un-blocks the order for shipping while the garment is
              still ruined, so it needs a reason — it is written to the production log.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="dismiss-reason">Reason</Label>
            <Input
              id="dismiss-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. customer cancelled the order"
            />
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Keep it queued
            </Button>
            <Button
              variant="destructive"
              disabled={reason.trim().length < 3 || dismiss.isPending}
              onClick={() => dismiss.mutate()}
            >
              {dismiss.isPending && <Loader2 className="size-4 animate-spin" />}
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
